import "./GroupedQueryBuilder.scss";

import Button from "components/Button/Button.tsx";
import type { OptionType } from "components/inputs";
import { Select } from "components/inputs";
import MultiSelect from "components/inputs/Select/MultiSelect";
import debounce from "lodash/debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useGetClientsQuery } from "services/client-api";
import { getBaseUrl } from "services/utils";
import { useToken } from "utils/context.ts";
import { useConstants } from "utils/hooks";

type ExtendedOptionType = OptionType & {
  clientId?: string;
};

type RuleType = "include" | "exclude";

type ClientServiceRule = {
  id: string;
  clients: ExtendedOptionType[]; // array of selected clients
  ruleType: RuleType;
  serviceTypes: string[];
};

type GroupedQueryMap = Record<
  string,
  { include?: string[] } | { exclude?: string[] }
>;

interface GroupedQueryBuilderProps {
  value: GroupedQueryMap;
  onChange: (value: GroupedQueryMap) => void;
  disabled?: boolean;
}

/**
 * GroupedQueryBuilder component
  aka Advanced, used in the Invoice Line Item Report
 */
const GroupedQueryBuilder: React.FC<GroupedQueryBuilderProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { accessToken } = useToken();
  const { constants } = useConstants();

  const [clientOptions, setClientOptions] = useState<ExtendedOptionType[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState<string>("");

  const [rules, setRules] = useState<ClientServiceRule[]>([]);
  const [clientsToFetch, setClientsToFetch] = useState<string[]>([]);

  // track if we're initializing to prevent calling onChange during init
  const isInitializingRef = useRef(false);

  // keep track of the last value and onChange function to avoid useEffect loop
  const lastValueRef = useRef<GroupedQueryMap>({});
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const debouncedSetClientSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setClientSearchTerm(value);
      }, 500),
    [],
  );

  const { data: clients, isFetching: isClientsFetching } = useGetClientsQuery(
    { searchTerm: clientSearchTerm },
    { skip: !clientSearchTerm },
  );

  // fetch clients by searching for them using the IDs we have
  const [fetchedClientsData, setFetchedClientsData] = useState<
    ExtendedOptionType[]
  >([]);

  useEffect(() => {
    if (clientsToFetch.length === 0 || !accessToken) {
      return;
    }

    // used when we load from URL state, need to fetch clients by ID
    const fetchClients = async () => {
      const clientPromises = clientsToFetch.map(async (clientId) => {
        try {
          const baseUrl = getBaseUrl();
          const response = await fetch(
            `${baseUrl}api/v1/clients/${clientId}/`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
              credentials: "include",
            },
          );
          if (response.ok) {
            const client = await response.json();
            return {
              label: `${client.client_id} - ${client.name}`,
              value: client.id.toString(),
              clientId: client.client_id,
            } as ExtendedOptionType;
          }
        } catch (error) {
          console.warn(`Failed to fetch client ${clientId}:`, error);
        }
        return null;
      });

      const results = await Promise.all(clientPromises);
      const validClients = results.filter(
        (client): client is ExtendedOptionType => client !== null,
      );
      setFetchedClientsData(validClients);
    };

    fetchClients();
  }, [clientsToFetch, accessToken]);

  const fetchedClients = fetchedClientsData;

  useEffect(() => {
    const searchOptions =
      clientSearchTerm && clients
        ? (clients.results?.map((client) => ({
            label: `${client.client_id} - ${client.name}`,
            value: client.id.toString(),
            clientId: client.client_id,
          })) ?? [])
        : [];

    // combine search results with fetched clients, avoiding duplicates
    const allOptions = [...fetchedClients];
    searchOptions.forEach((option) => {
      if (!allOptions.find((existing) => existing.value === option.value)) {
        allOptions.push(option);
      }
    });

    setClientOptions(allOptions);
  }, [clients, clientSearchTerm, fetchedClients]);

  // service type options from constants
  const serviceTypeOptions = useMemo(() => {
    const serviceTypes = constants?.SERVICE_TYPE || [];
    // filter out "-- Select --" option and add "ALL" option
    const filteredServiceTypes = serviceTypes.filter(
      (option) =>
        option.label !== "-- Select --" && option.value !== "-- Select --",
    );
    return [
      { label: "All Service Types", value: "ALL" },
      ...filteredServiceTypes,
    ];
  }, [constants]);

  const ruleTypeOptions: OptionType[] = [
    { label: "Include Only", value: "include" },
    { label: "Exclude", value: "exclude" },
  ];

  // get available client options for a specific rule
  // excludes clients that are already selected in OTHER rules
  const getAvailableClientOptions = (currentRuleId: string) => {
    const currentRule = rules.find((rule) => rule.id === currentRuleId);
    const currentRuleClientIds = new Set(
      currentRule?.clients.map((client) => client.value.toString()) || [],
    );

    const otherRulesClientIds = new Set<string>();
    rules.forEach((rule) => {
      if (rule.id !== currentRuleId) {
        rule.clients.forEach((client) => {
          otherRulesClientIds.add(client.value.toString());
        });
      }
    });

    // filter out clients that are already selected in OTHER rules
    // and the ones already selected in THIS rule
    return clientOptions.filter(
      (option) =>
        !otherRulesClientIds.has(option.value.toString()) ||
        currentRuleClientIds.has(option.value.toString()),
    );
  };

  // initialize rules from value prop - only run when value actually changes AND we don't have local rules
  useEffect(() => {
    const groupedQuery: GroupedQueryMap = value || {};

    // check if the value has actually changed
    if (JSON.stringify(groupedQuery) === JSON.stringify(lastValueRef.current)) {
      return;
    }

    // don't reinitialize if we already have rules (even without clients) to prevent clearing
    if (rules.length > 0) {
      return;
    }

    lastValueRef.current = groupedQuery;
    isInitializingRef.current = true;

    // extract client IDs that need to be fetched (exclude special ALL_CLIENTS key)
    const clientIdsToFetch = Object.keys(groupedQuery).filter(
      (id) => id && id !== "ALL_CLIENTS",
    );

    if (clientIdsToFetch.length > 0) {
      setClientsToFetch(clientIdsToFetch);
    }

    const newRules: ClientServiceRule[] = [];

    Object.entries(groupedQuery).forEach(([clientId, ruleData]) => {
      if ("include" in ruleData && ruleData.include) {
        newRules.push({
          id: `rule-${clientId}-include`,
          clients: [], // will be populated once client data is fetched
          ruleType: "include",
          serviceTypes: ruleData.include,
        });
      } else if ("exclude" in ruleData && ruleData.exclude) {
        newRules.push({
          id: `rule-${clientId}-exclude`,
          clients: [], // will be populated once client data is fetched
          ruleType: "exclude",
          serviceTypes: ruleData.exclude,
        });
      }
    });

    setRules(newRules);

    // reset initialization flag after a microtask to allow the rules effect to see it
    setTimeout(() => {
      isInitializingRef.current = false;
    }, 0);
  }, [value]); // removed rules from dependency to prevent re-initialization loop

  // populate rules with client data once it's fetched
  useEffect(() => {
    if (fetchedClients.length === 0 || rules.length === 0) {
      return;
    }

    // check if any rules need client data populated
    const rulesNeedingClients = rules.filter(
      (rule) =>
        rule.clients.length === 0 &&
        rule.id.includes("rule-") &&
        (rule.id.includes("-include") || rule.id.includes("-exclude")),
    );

    if (rulesNeedingClients.length === 0) {
      return;
    }
    setRules((prevRules) =>
      prevRules.map((rule) => {
        // extract client ID from rule ID
        const match = rule.id.match(/rule-(\d+)-(include|exclude)/);
        if (!match) return rule;

        const [, clientDbId] = match;
        const clientOption = fetchedClients.find(
          (client) => client.value === clientDbId,
        );

        if (clientOption && rule.clients.length === 0) {
          return {
            ...rule,
            clients: [clientOption],
          };
        }

        return rule;
      }),
    );
  }, [fetchedClients]); // removed rules from dependency to prevent excessive re-runs

  // update parent when rules change - use manual conversion to avoid function dependencies
  useEffect(() => {
    // don't call onChange if we're initializing from the parent value
    if (isInitializingRef.current) {
      return;
    }

    const groupedQuery: GroupedQueryMap = {};

    // convert rules to grouped query format - each client gets its own entry
    rules.forEach((rule) => {
      if (rule.clients.length === 0) {
        // when no clients are selected, pass ALL to the backend
        if (rule.ruleType === "include") {
          groupedQuery["ALL_CLIENTS"] = { include: rule.serviceTypes };
        } else if (rule.ruleType === "exclude") {
          groupedQuery["ALL_CLIENTS"] = { exclude: rule.serviceTypes };
        }
      } else {
        rule.clients.forEach((client) => {
          const clientDbId = client.value.toString();
          if (rule.ruleType === "include") {
            groupedQuery[clientDbId] = { include: rule.serviceTypes };
          } else if (rule.ruleType === "exclude") {
            groupedQuery[clientDbId] = { exclude: rule.serviceTypes };
          }
        });
      }
    });

    onChangeRef.current(groupedQuery);
  }, [rules]);

  const addNewRule = () => {
    if (rules.length >= 50) {
      return;
    }

    const newRule: ClientServiceRule = {
      id: `rule-${Date.now()}`,
      clients: [],
      ruleType: "include",
      serviceTypes: ["ALL"], // default to "All Service Types"
    };
    setRules([...rules, newRule]);
  };

  const updateRule = (ruleId: string, updates: Partial<ClientServiceRule>) => {
    setRules((prevRules) =>
      prevRules.map((rule) =>
        rule.id === ruleId ? { ...rule, ...updates } : rule,
      ),
    );
  };

  const removeRule = (ruleId: string) => {
    setRules((prevRules) => prevRules.filter((rule) => rule.id !== ruleId));
  };

  const clearAllRules = () => {
    setRules([]);
    // immediately notify parent of empty state to avoid race condition
    onChangeRef.current({});
  };

  return (
    <>
      {/* Rules list */}
      <div className="rules-section">
        {rules.length === 0 ? (
          <div className="no-rules-message">
            <p>
              No filtering rules defined. Click "Add Rule" to create a rule.
            </p>
          </div>
        ) : (
          <div className="rules-list">
            {rules.map((rule, index) => (
              <div key={rule.id} className="rule-item">
                <div className="rule-header">
                  <span className="rule-number">Rule {index + 1}</span>
                  <Button
                    type="button"
                    onClick={() => removeRule(rule.id)}
                    className="remove-rule-btn"
                    disabled={disabled}
                  >
                    <span className="material-icons">close</span>
                  </Button>
                </div>

                <div className="rule-content">
                  <div className="rule-row">
                    <div className="client-select">
                      <label>Clients</label>

                      <MultiSelect
                        name={`clients-${rule.id}`}
                        options={getAvailableClientOptions(rule.id)}
                        value={rule.clients}
                        placeholder="Search for clients..."
                        noOptionsMessage={({ inputValue }) =>
                          !inputValue
                            ? "Begin typing to search for clients..."
                            : isClientsFetching
                              ? "Searching..."
                              : "No clients found or already in another rule."
                        }
                        onInputChange={(inputValue: string) =>
                          debouncedSetClientSearchTerm(inputValue)
                        }
                        onChange={(selectedOptions: OptionType[]) => {
                          updateRule(rule.id, {
                            clients: selectedOptions as ExtendedOptionType[],
                          });
                        }}
                        isDisabled={disabled}
                        widthVariant="full"
                        disableRemoveCondition={false}
                      />
                    </div>

                    <div className="rule-type-select">
                      <label>Rule Type</label>
                      <Select
                        name={`rule-type-${rule.id}`}
                        options={ruleTypeOptions}
                        value={
                          ruleTypeOptions.find(
                            (option) => option.value === rule.ruleType,
                          ) || null
                        }
                        onChange={(selectedOption: OptionType | null) => {
                          if (selectedOption) {
                            updateRule(rule.id, {
                              ruleType: selectedOption.value as RuleType,
                            });
                          }
                        }}
                        placeholder="Select rule type..."
                        isDisabled={disabled}
                      />
                    </div>

                    <div className="service-types-select">
                      <label>Service Types</label>
                      <MultiSelect
                        name={`service-types-${rule.id}`}
                        options={serviceTypeOptions}
                        value={serviceTypeOptions.filter((option) =>
                          rule.serviceTypes.includes(String(option.value)),
                        )}
                        onChange={(selectedOptions: OptionType[]) => {
                          updateRule(rule.id, {
                            serviceTypes: selectedOptions.map((option) =>
                              String(option.value),
                            ),
                          });
                        }}
                        placeholder="Select service types..."
                        isDisabled={disabled}
                        disableRemoveCondition={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* action buttons */}
      <div className="actions-section">
        <Button
          type="button"
          onClick={addNewRule}
          disabled={disabled || rules.length >= 50}
          className="add-rule-btn"
        >
          <span className="material-icons">add</span>
          Add Rule {rules.length >= 50 ? "(Max 50)" : `(${rules.length}/50)`}
        </Button>

        {rules.length > 0 && (
          <Button
            type="button"
            onClick={clearAllRules}
            disabled={disabled}
            className="clear-rules-btn"
          >
            <span className="material-icons">clear_all</span>
            Clear All Rules
          </Button>
        )}
      </div>
    </>
  );
};

export default GroupedQueryBuilder;
